import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { Roles, Role } from '@core/constants/roles.constant';
import { Group } from '@models/group.model';
import { isEmailValid } from '@helper/validators';
import { Datastore } from '@services/datastore';
import { MemberService } from '@services/member.service';
import { SnackBarService } from '@services/snack-bar.service';
import { BulkInvitation } from '@models/auth/bulk-invitation.model';
import { Resource } from '@shared/multi-select-server-side-search/interfaces';
import { multiSelectStartTopPosition } from '@core/constants/multi-select-position.constant';

@Component({
  selector: 'app-member-invite-email-dialog',
  templateUrl: './member-invite-email-dialog.component.html',
  styleUrls: ['./member-invite-email-dialog.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,

  standalone: false,
})
export class MemberInviteEmailDialogComponent {
  public selectedGroups: Group[] = [];
  public ROLES = Roles;
  public readonly assignableRoles = signal<Role[]>([]);
  public memberInviteByEmailForm: UntypedFormGroup;
  public Group = Group;
  public multiSelectPosition = multiSelectStartTopPosition;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private translateService: TranslateService,
    private snackbarService: SnackBarService,
    private formBuilder: UntypedFormBuilder,
    private datastore: Datastore,
    private memberService: MemberService,
    public dialogRef: MatDialogRef<MemberInviteEmailDialogComponent>,
  ) {
    this.memberInviteByEmailForm = this.formBuilder.group({
      email: new UntypedFormControl('', [
        Validators.required,
        this.emailsListValid(),
        this.emailsListUnique(),
      ]),
      role: new UntypedFormControl('', [Validators.required]),
    });

    effect(() => {
      const method = this.assignableRoles().length ? 'enable' : 'disable';
      this.memberInviteByEmailForm.controls.role[method]();
    });

    this.memberService
      .getAssignableRoles()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(roles => this.assignableRoles.set(roles));
  }

  sendInvites(): void {
    const controls = this.memberInviteByEmailForm.controls;
    const data = {
      emails: this.getEmailsList(controls.email),
      role: controls.role.value,
      groups: this.selectedGroups,
    };
    const profileInvite = this.datastore.createRecord(BulkInvitation, data);
    profileInvite
      .save()
      .pipe(take(1))
      .subscribe(
        () => {
          this.showSuccessNotification();
          this.memberInviteByEmailForm.reset();
          this.selectedGroups = [];
        },
        () => this.showErrorNotification(),
      );
  }

  onFilter(resources: Resource[]): void {
    const groups = resources as Group[];
    this.selectedGroups = groups;
  }

  private getEmailsList(control: AbstractControl): string[] {
    return control.value.replace(/\s/g, '').split(',');
  }

  private emailsListValid(): ValidatorFn {
    // tslint:disable-next-line:no-any
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const emailsList = this.getEmailsList(control);
      const isInvalid = emailsList.some(
        (email: string) => !isEmailValid(email),
      );
      return isInvalid ? { invalidEmail: { value: control.value } } : null;
    };
  }

  private emailsListUnique(): ValidatorFn {
    // tslint:disable-next-line:no-any
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const emailsList = this.getEmailsList(control);
      const isInvalid = emailsList.find(
        (email: string, index: number) => emailsList.indexOf(email) !== index,
      );
      return isInvalid ? { duplicateEmail: { value: control.value } } : null;
    };
  }

  private showSuccessNotification(): void {
    this.snackbarService.success(
      this.translateService.instant(
        'attributes.talent.invite.successInvitesMessage',
      ),
    );
    this.dialogRef.close();
  }

  private showErrorNotification(): void {
    this.snackbarService.error(
      this.translateService.instant(
        'attributes.talent.invite.errorInvitesMessage',
      ),
    );
    this.dialogRef.close();
  }
}
